/**
 * levelgen/campaign.ts — The thousand levels the menu offers.
 *
 * A campaign is just a list of (seed, difficulty) pairs run through the
 * generator, so every level is built by the same pipeline and held to the same
 * guarantees as any other: reachable bin, reachable home, crossable streets, and
 * a rating that agrees with its label.
 *
 * ## Why the seeds are written down
 *
 * "Level 4" has to mean the same town on every machine, forever — otherwise a
 * player's progress is meaningless, two people can't compare notes, and a bug
 * report about level 4 is unreproducible. So the seed is `walk-N`, fixed by the
 * level number and derived from nothing else: not the clock, not the player, and
 * not the position of the level in the ramp. Re-shaping the ramp therefore
 * changes how hard level 700 is, but the thousand seeds themselves never move.
 *
 * ## The shape of the ramp
 *
 * A thousand levels is too many to write out one by one, and a formula that
 * turns a level number into a difficulty is too opaque to design with. So the
 * campaign is written as **chapters**: a run of levels with a fixed pattern of
 * bands cycled through it. That keeps the thing a designer actually cares
 * about — the *mix* — literal and countable, while a hundred levels cost one
 * line each rather than a hundred.
 *
 * Two rules shape the mix:
 *
 * - **The bands arrive in order, and then stay.** Nothing harder than medium
 *   before level 11, so the first ten teach the leash, the crossing and the
 *   errand without punishing you for learning. Hard from 11. Very hard from 26.
 * - **It varies rather than ramps.** Once a band is in the mix it never leaves,
 *   and a hard level is followed by an easier one often enough that the campaign
 *   breathes. The *centre of gravity* climbs — chapter by chapter there is more
 *   very hard and less easy — but level 400 being a medium breather after a very
 *   hard 399 is the point, not a bug. A monotone ramp over a thousand levels is
 *   a thousand levels of the same feeling, slowly.
 */
import { generateLevel } from './generate';
import { tutorialLevel } from './tutorial';
import type { Difficulty, LevelSpec } from './types';

/** How many levels the campaign has. */
export const CAMPAIGN_LENGTH = 1000;

/**
 * The guided walk: the one level that teaches the game.
 *
 * Level 1 and not a separate screen in front of the campaign, because a
 * tutorial nobody can replay is a tutorial nobody can go back to — and a first
 * level that teaches nothing is a wasted one. It is still an easy campaign
 * level with a tick of its own; what it adds is a script (`systems/tutorial.ts`)
 * and a calmer town (`levelgen/tutorial.ts`).
 */
export const TUTORIAL_LEVEL = 1;

/** One entry in the campaign: which level, how hard, and from what seed. */
export interface CampaignEntry {
  /** 1-based, as the player sees it. */
  number: number;
  difficulty: Difficulty;
  seed: string;
  /** True for the guided walk. Named "Tutorial" rather than by its band. */
  tutorial?: boolean;
}

/**
 * A run of levels and the pattern of bands cycled across it.
 *
 * `from` is the first level in the chapter; the chapter runs until the next one
 * starts. Level `n` takes `pattern[(n - from) % pattern.length]`, so a pattern
 * shorter than its chapter simply repeats — the pattern is a bar of music, not a
 * one-off. Patterns are written long enough (15–25 levels) that the repeat is
 * never audible over a session, and the counts in each comment are what the
 * chapter is actually *for*.
 */
interface Chapter {
  from: number;
  /** What this stretch of the campaign is meant to feel like. */
  name: string;
  pattern: readonly Difficulty[];
}

/**
 * The campaign, chapter by chapter.
 *
 * The first three chapters are written to cover their levels exactly — those are
 * the levels a player meets while the game is still introducing itself, and each
 * one is placed rather than dealt. From level 51 the chapters are long and the
 * patterns cycle.
 */
const CHAPTERS: readonly Chapter[] = [
  {
    from: 1,
    // Levels 1–10. The teaching stretch: easy and medium only, so the first
    // hour is about learning to hold a dog rather than surviving traffic.
    // 4 easy · 6 medium
    name: 'first walks',
    pattern: [
      'easy',
      'easy',
      'easy',
      'medium',
      'medium',
      'medium',
      'easy',
      'medium',
      'medium',
      'medium',
    ],
  },
  {
    from: 11,
    // Levels 11–25. Hard arrives, on level 11 and regularly after it, but it is
    // still the exception — 15 and 21 are the breathers that make 22 land.
    // 2 easy · 7 medium · 6 hard
    name: 'busier streets',
    pattern: [
      'hard',
      'medium',
      'medium',
      'hard',
      'easy',
      'medium',
      'hard',
      'medium',
      'medium',
      'hard',
      'easy',
      'medium',
      'hard',
      'medium',
      'hard',
    ],
  },
  {
    from: 26,
    // Levels 26–50. Very hard enters on 26 and stays an event rather than a
    // routine — roughly one level in six, with hard and medium sharing the rest
    // evenly between them. 3 easy · 9 medium · 9 hard · 4 very hard
    name: 'rush hour',
    pattern: [
      'very-hard',
      'medium',
      'hard',
      'medium',
      'hard',
      'easy',
      'medium',
      'hard',
      'medium',
      'very-hard',
      'hard',
      'medium',
      'hard',
      'easy',
      'medium',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'medium',
      'easy',
      'hard',
      'medium',
      'very-hard',
      'hard',
    ],
  },
  {
    from: 51,
    // Levels 51–150. Hard becomes the commonest band and medium the steady
    // second, while very hard stays a spike rather than a plateau.
    // 2 easy · 7 medium · 8 hard · 3 very hard, per 20.
    name: 'the long middle',
    pattern: [
      'medium',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'easy',
      'medium',
      'hard',
      'medium',
      'very-hard',
      'hard',
      'medium',
      'easy',
      'hard',
      'medium',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'hard',
    ],
  },
  {
    from: 151,
    // Levels 151–350. Very hard ticks up by one in twenty; medium and hard are
    // level with each other, which is what stops the middle of the campaign
    // feeling like one long climb. 2 easy · 7 medium · 7 hard · 4 very hard,
    // per 20.
    name: 'across town',
    pattern: [
      'hard',
      'very-hard',
      'medium',
      'hard',
      'medium',
      'easy',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'medium',
      'easy',
      'very-hard',
      'hard',
      'medium',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'medium',
    ],
  },
  {
    from: 351,
    // Levels 351–600. A quarter of the levels are very hard now, and the easy
    // ones have not gone anywhere — 357 and 362 are still breathers.
    // 2 easy · 6 medium · 7 hard · 5 very hard, per 20.
    name: 'the far side',
    pattern: [
      'very-hard',
      'hard',
      'medium',
      'very-hard',
      'hard',
      'medium',
      'easy',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'easy',
      'very-hard',
      'hard',
      'medium',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'medium',
    ],
  },
  {
    from: 601,
    // Levels 601–850. Very hard draws level with hard, but never passes it: a
    // campaign that is mostly its top band has nowhere left to go, and a player
    // meeting one every other walk stops reading it as a step up.
    // 1 easy · 6 medium · 7 hard · 6 very hard, per 20.
    name: 'night shift',
    pattern: [
      'very-hard',
      'hard',
      'medium',
      'very-hard',
      'hard',
      'medium',
      'very-hard',
      'hard',
      'easy',
      'very-hard',
      'medium',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'very-hard',
      'medium',
      'hard',
      'medium',
      'hard',
    ],
  },
  {
    from: 851,
    // Levels 851–1000. The last stretch leans on hard rather than very hard —
    // about two very hard walks in seven, which is enough to make the finish
    // feel like one without turning it into a wall. The pattern is 25 long so
    // that level 1000 lands on its final note: the campaign ends on a very hard
    // walk rather than wherever the maths fell.
    // 1 easy · 6 medium · 11 hard · 7 very hard, per 25.
    name: 'the last mile',
    pattern: [
      'very-hard',
      'hard',
      'medium',
      'hard',
      'very-hard',
      'hard',
      'medium',
      'hard',
      'very-hard',
      'hard',
      'medium',
      'easy',
      'hard',
      'very-hard',
      'hard',
      'medium',
      'hard',
      'very-hard',
      'hard',
      'medium',
      'hard',
      'very-hard',
      'hard',
      'medium',
      'very-hard',
    ],
  },
];

/**
 * Which band level `n` is, straight from the chapters.
 *
 * Throws outside the campaign rather than clamping: a level number that isn't in
 * the campaign is a caller's bug, and silently handing back the difficulty of
 * level 1000 would hide it.
 */
export function difficultyFor(number: number): Difficulty {
  if (!Number.isInteger(number) || number < 1 || number > CAMPAIGN_LENGTH) {
    throw new RangeError(`level ${number} is not in the campaign (1–${CAMPAIGN_LENGTH})`);
  }
  // Last chapter that has started by this level. A linear scan over eight
  // entries; there is nothing here worth a binary search.
  let chapter = CHAPTERS[0];
  for (const next of CHAPTERS) {
    if (next.from <= number) chapter = next;
  }
  return chapter.pattern[(number - chapter.from) % chapter.pattern.length];
}

/** The seed level `n` is built from. Fixed forever — see the note up top. */
function seedFor(number: number): string {
  return `walk-${number}`;
}

/** The entry for one level, or `undefined` if it isn't in the campaign. */
export function campaignEntry(number: number): CampaignEntry | undefined {
  if (!Number.isInteger(number) || number < 1 || number > CAMPAIGN_LENGTH) return undefined;
  return {
    number,
    difficulty: difficultyFor(number),
    seed: seedFor(number),
    ...(number === TUTORIAL_LEVEL ? { tutorial: true } : {}),
  };
}

/**
 * A run of consecutive entries, starting at `first` and at most `count` long.
 *
 * What the menu draws: it shows a page at a time, and building all thousand
 * entries to throw 950 of them away on every rebuild is work nobody asked for.
 * Out-of-range levels are skipped rather than refused, so the last page can be
 * short without the caller doing arithmetic.
 */
export function campaignSlice(first: number, count: number): CampaignEntry[] {
  const out: CampaignEntry[] = [];
  for (let n = first; n < first + count; n++) {
    const entry = campaignEntry(n);
    if (entry) out.push(entry);
  }
  return out;
}

/** Every level in the campaign, in order. Cheap: no towns are built here. */
export function campaignPlan(): CampaignEntry[] {
  return campaignSlice(1, CAMPAIGN_LENGTH);
}

/**
 * Levels already built, keyed by number, most-recently-built last.
 *
 * Generation is deterministic, so this is purely a speed cache — picking level
 * 4, dying, and picking it again rebuilds the identical town either way. It
 * matters because the menu is a click away from gameplay and a very-hard town
 * takes long enough to build that the second visit would stutter.
 *
 * It is **capped**, which it did not need to be when the campaign was ten
 * levels long: a `LevelSpec` is a few tens of kilobytes of streets, plots and
 * trees, and a cache with no lid would hold every town a long session ever
 * visited. The cap is far above the handful of levels anyone bounces between in
 * one sitting, so the stutter it exists to prevent stays prevented.
 */
const built = new Map<number, LevelSpec>();

/** How many built towns to keep. Comfortably more than a session revisits. */
const CACHE_LIMIT = 32;

/**
 * Build (or fetch) a campaign level.
 *
 * Throws for a number outside the campaign — that is a programming error rather
 * than a player one, since the menu only ever offers the thousand that exist.
 */
export function campaignLevel(number: number): LevelSpec {
  const cached = built.get(number);
  if (cached) {
    // Re-insert so the levels being played stay the ones that are kept.
    built.delete(number);
    built.set(number, cached);
    return cached;
  }

  const entry = campaignEntry(number);
  if (!entry) {
    throw new RangeError(`level ${number} is not in the campaign (1–${CAMPAIGN_LENGTH})`);
  }

  // The guided walk is built by its own module — same seed and band, two
  // calmer schedules on top. Everything after this line treats it as an
  // ordinary campaign level, which is exactly what it is.
  const level = entry.tutorial
    ? tutorialLevel()
    : generateLevel({ seed: entry.seed, difficulty: entry.difficulty });
  built.set(number, level);
  // Map iterates in insertion order, so this walks the least recently built or
  // played first — and deleting as it goes is well defined, since a key removed
  // before the iterator reaches it is simply skipped.
  for (const oldest of built.keys()) {
    if (built.size <= CACHE_LIMIT) break;
    built.delete(oldest);
  }
  return level;
}

/** Drop the cache. Only tests need this; the game builds each level once. */
export function forgetCampaignLevels(): void {
  built.clear();
}

/** How a band is written in the menu. */
export const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  'very-hard': 'Very hard',
};

/** What the guided walk is called instead of its band. */
export const TUTORIAL_LABEL = 'Tutorial';

/**
 * What a level is called under its number — in the menu and in the HUD.
 *
 * Takes anything carrying a band and the tutorial flag, so the picker can label
 * an entry it has not built and the HUD can label the spec it is playing,
 * without either of them re-deciding what the guided walk is called.
 */
export function bandLabel(level: { difficulty: Difficulty; tutorial?: boolean }): string {
  return level.tutorial ? TUTORIAL_LABEL : DIFFICULTY_LABEL[level.difficulty];
}
